import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SIDEBAR_CONFIG, SidebarItem } from 'src/app/private/services/module.config';
import { PermissionService } from 'src/app/private/services/permission.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {

  sidebarConfig: SidebarItem[] = SIDEBAR_CONFIG;

  mobileSidebarOpen = false;
  sidepanel = 'hide_panel';

  activeParentKey: string | null = null;
  hoverTimeout: any;

  mobileSubmenus: { [key: string]: boolean } = {};

  constructor(
    private router: Router,
    private elementRef: ElementRef,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    // Ensure permissions are loaded
    this.permissionService.refresh();
    // console.log("permissions",this.permissionService.permissions);
    // console.log("modules",this.permissionService.modules);

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.syncActiveMenu());

    setTimeout(() => this.syncActiveMenu(), 100);
  }

  /* ===============================
     ACCESS CHECKS (Using Service)
  =============================== */

  hasModuleAccess(module: string): boolean {
    return this.permissionService.getAccess(module);
  }

  hasFeatureAccess(module: string, feature?: string): boolean {
    if (!feature) return this.hasModuleAccess(module);
    return this.permissionService.getfeatureAccess(module, feature);
  }

  hasChildAccess(item: SidebarItem): boolean {
    if (!item.children) return false;
    return item.children.some((child: any) => this.hasModuleAccess(child.module));
  }

  canShow(item: SidebarItem): boolean {
    // If no module specified, always show
    if (!item.module) return true;

    // Use feature_modules (hospital-level) to control sidebar visibility
    return this.permissionService.getFeatureModuleAccess(item.module);
  }

  canChildShow(module: any, child: any): boolean {
    if (!child.module) return true;
    // Normalize key: replace all hyphens with underscores
    const normalizedKey = child.key.replace(/-/g, '_');
    return this.permissionService.getFeatureModuleAccess(normalizedKey);
  }

  /* ===============================
     OPTIONAL: Filter Sidebar Directly
  =============================== */

  get filteredSidebar(): SidebarItem[] {
    return this.sidebarConfig.filter(item => this.canShow(item));
  }

  /* ===============================
     DESKTOP INTERACTION
  =============================== */

  onMouseEnter(item: SidebarItem): void {
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);

    if (item.children?.length) {
      this.activeParentKey = item.key;
      this.sidepanel = 'show_panel';
    } else {
      this.activeParentKey = null;
      this.sidepanel = 'hide_panel';
    }
  }

  onMouseLeave(): void {
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);

    this.hoverTimeout = setTimeout(() => {
      this.sidepanel = 'hide_panel';
      this.activeParentKey = null;
    }, 300);
  }

  keepPanelOpen(): void {
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
    this.sidepanel = 'show_panel';
  }

  /* ===============================
     MOBILE
  =============================== */

  toggleMobileSidebar(): void {
    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  toggleMobileSubmenu(key: string): void {
    this.mobileSubmenus[key] = !this.mobileSubmenus[key];
  }

  isMobileSubmenuOpen(key: string): boolean {
    return !!this.mobileSubmenus[key];
  }

  toggleParent(key: string): void {
    const item = this.sidebarConfig.find(i => i.key === key);

    if (!item || !item.children?.length) {
      this.activeParentKey = null;
      this.sidepanel = 'hide_panel';
      return;
    }

    if (this.activeParentKey === key) {
      this.activeParentKey = null;
      this.sidepanel = 'hide_panel';
    } else {
      this.activeParentKey = key;
      this.sidepanel = 'show_panel';
    }
  }

  /* ===============================
     HELPERS
  =============================== */

  get activeParentItem(): SidebarItem | undefined {
    return this.sidebarConfig.find(i => i.key === this.activeParentKey);
  }

  isParentActive(key: string): boolean {
    return this.activeParentKey === key;
  }

  syncActiveMenu(): void {
    const url = this.router.url;

    for (const item of this.sidebarConfig) {
      if (item.children?.some(c => c.route && url.includes(c.route))) {
        return;
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: Event): void {
    if (
      this.mobileSidebarOpen &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.mobileSidebarOpen = false;
    }
  }
}
